/*
 * @providesModule PlayableMatchesStore
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import AWSManager from 'AWSManager';
import BlockedUsersStore from 'BlockedUsersStore';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import FriendsTournamentApiController from 'FriendsTournamentApiController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import LoadingIndicator from 'LoadingIndicator';
import _ from 'lodash';
import MatchSelectListSection from 'MatchSelectListSection';
import MinFlux from 'min-flux';
import moment from 'moment';
import Preferences from 'Preferences';
import RESTStore, {isWithinBackgroundResumeGracePeriod} from 'RESTStore';
import Sizes, {IPHONE_X_HEIGHT} from 'Sizes';
import SKZConstants from 'SKZConstants';
import SKZLiveEventTemplateSet from 'SKZLiveEventTemplateSet';
import Tournament from 'SKZTournament';
import {SKZTournamentCategoryRecord} from 'SKZTournamentCategories';

import FTUEConversionsController from 'FTUEConversionsController';
import FTUEController from 'FTUEController';
import {AppState, NativeModules, Platform} from 'react-native';
import ErrorDialogModal from 'ErrorDialogModal';
import ModalNavigationController from 'ModalNavigationController';
import React from 'react';
import DdLogs from 'DdLogsWrapper';
import {getErrorMessage} from 'DataDogErrorUtils';
import {
  getNewUserMatchFilter,
  INCLUDE_ALL_SECTIONS,
  MatchSection,
  MatchSectionFilterFunction,
  NewUserMatchFilter,
} from './MatchUtils';
import {
  MATCH_SELECT_COLLAPSE_SECTION,
  MATCH_SELECT_OPEN_SECTION,
  SECTION_TEMPLATE_EXPAND_VIEW_CLICK,
} from 'AWSConstants';
import {TournamentRecord} from 'app/ObjectModel/SKZTournament';
import LiveActivityManager from 'LiveActivityManager';

const CASH = 'CASH';
const LIVE = 'LIVE';
const FRIENDS = 'FRIENDS';
const SYNC = 'SYNC';
const Z_TEMPLATE = 'Z';

const SKZAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;

const CATEGORY_KEYS = '@MatchSelectDataSource:_expandedCategories';

const PlayableMatchesStore = new MinFlux.Store({
  _expandedCategories: {},
  _highlightedMatch: null,
  _highlightedMatchText: null,
  _shouldDisplaySearchResults: false,
  _seachResults: null,
  _isFetching: false,
  _matches: null,
  _totalGamesPlayed: null,
  _newUserMatchFilter: NewUserMatchFilter.NONE,
  _userId: null,
  _joinDate: null,
  _zBalance: 0,
  _expandedHeightOffset: 0,
  _liveActivityStarted: false,
  _hasDiscountUpdates: false,
  _lastFetchError: null as {error: unknown; timestamp: number; context: string} | null,
  _fetchRetryCount: 0,
  _maxRetries: 2,
  _pendingRetryOnInteract: false, // Track if we need to retry when shouldInteractWithInterface becomes true
  _nativeStateListener: null as ReturnType<typeof GlobalEventEmitter.addListener> | null,

  /**
   * Logs tournament fetch errors with comprehensive debugging info for Datadog
   * @param error - The error object
   * @param context - Where the error occurred (e.g., 'fetchTournamentCategories', 'fetchMatchList')
   * @param additionalInfo - Any additional context to include in the log
   */
  _logTournamentError(
    error: unknown,
    context: string,
    additionalInfo: Record<string, unknown> = {}
  ) {
    const currentAppState = AppState.currentState;

    // Get actual native shouldInteractWithInterface value for accurate logging
    if (Platform.OS === 'ios' && SkillzModule?.shouldInteractWithInterface) {
      SkillzModule.shouldInteractWithInterface(
        (shouldInteract: boolean, isMatchInProgress: boolean) => {
          this._sendErrorToDatadog(error, context, additionalInfo, {
            appState: currentAppState,
            shouldInteractWithInterface: shouldInteract,
            isMatchInProgress,
          });
        }
      );
    } else {
      // Android or fallback
      this._sendErrorToDatadog(error, context, additionalInfo, {
        appState: currentAppState,
        shouldInteractWithInterface: null, // Not available on Android
        isMatchInProgress: null,
      });
    }

    // Store for potential UI display
    this._lastFetchError = {
      error,
      timestamp: Date.now(),
      context,
    };
  },

  /**
   * Internal method to send error to Datadog
   * All fields are flattened to avoid [object Object] in Datadog logs
   */
  _sendErrorToDatadog(
    error: unknown,
    context: string,
    additionalInfo: Record<string, unknown>,
    nativeState: {
      appState: string;
      shouldInteractWithInterface: boolean | null;
      isMatchInProgress: boolean | null;
    }
  ) {
    DdLogs.warn('[TOURNAMENT_FETCH_ERROR]', {
      timestamp: new Date().toISOString(),
      context,
      platform: Platform.OS,
      appState: nativeState.appState,
      shouldInteractWithInterface: nativeState.shouldInteractWithInterface,
      isMatchInProgress: nativeState.isMatchInProgress,
      retryCount: this._fetchRetryCount,
      hasData: this._data instanceof Array,
      dataLength: this._data?.length || 0,
      // Flatten error fields to avoid [object Object]
      errorMessage: getErrorMessage(error),
      errorCode: (error as any)?.code ?? null,
      errorStatusCode: (error as any)?.statusCode ?? (error as any)?.status ?? null,
      ...additionalInfo,
    });
  },

  /**
   * Shows an error dialog to the user when tournament fetch fails
   * Only shows if we've exhausted retries
   */
  _showTournamentFetchErrorDialog(retryCallback?: () => void) {
    const errorMessage = I18n.message(
      "We're having trouble loading tournaments. Please check your connection and try again."
    );

    const errorDialog = (
      <ErrorDialogModal
        message={errorMessage}
        retryCallback={() => {
          ModalNavigationController.hideModal();
          if (retryCallback) {
            retryCallback();
          }
        }}
        onDone={() => {
          ModalNavigationController.hideModal();
        }}
        error={this._lastFetchError?.error}
      />
    );

    ModalNavigationController.showModal(errorDialog, {
      animationType: 'fade' as const,
      transparent: true,
    });
  },

  /**
   * Resets the retry counter - should be called on successful fetch
   */
  _resetRetryState() {
    this._fetchRetryCount = 0;
    this._lastFetchError = null;
    this._pendingRetryOnInteract = false;
  },

  /**
   * Initializes the native state listener to retry failed fetches
   * when shouldInteractWithInterface becomes true (user returns to home screen).
   * Should be called once at app startup.
   */
  initializeNativeStateListener() {
    // Only set up listener on iOS and if not already set
    if (Platform.OS !== 'ios' || this._nativeStateListener) {
      return;
    }

    this._nativeStateListener = GlobalEventEmitter.addListener(
      'ShouldInteractWithInterfaceBecameTrue',
      (data: {shouldInteractWithInterface: boolean; isMatchInProgress: boolean; timestamp: number}) => {

        // If we have a pending retry and user is now on home screen, retry the fetch
        if (this._pendingRetryOnInteract && data.shouldInteractWithInterface && !data.isMatchInProgress) {
          DdLogs.info('[TOURNAMENT_FETCH_RETRY] Retrying tournament fetch after shouldInteractWithInterface became true', {
            timestamp: new Date().toISOString(),
            lastErrorTimestamp: this._lastFetchError?.timestamp,
            lastErrorContext: this._lastFetchError?.context,
            timeSinceError: this._lastFetchError ? Date.now() - this._lastFetchError.timestamp : null,
          });

          this._pendingRetryOnInteract = false;
          this._fetchRetryCount = 0; // Reset retry count for fresh attempt
          this.fetchMatchList(false, false);
        }
      }
    );

  },

  /**
   * Cleans up the native state listener.
   * Should be called when the store is no longer needed.
   */
  cleanupNativeStateListener() {
    if (this._nativeStateListener) {
      this._nativeStateListener.remove();
      this._nativeStateListener = null;
    }
  },

  /**
   * Marks that we should retry the fetch when shouldInteractWithInterface becomes true.
   * Called when fetch fails and retries are exhausted.
   */
  _markPendingRetryOnInteract() {
    if (Platform.OS === 'ios') {
      this._pendingRetryOnInteract = true;
      DdLogs.info('[TOURNAMENT_FETCH_PENDING] Marked pending retry for when user returns to home screen', {
        timestamp: new Date().toISOString(),
        lastErrorContext: this._lastFetchError?.context,
      });
    }
  },

  fetchMatchList(showLoading = false, isFTUE = false) {
    if (!this._isFetching) {
      this._isFetching = true;
      if (showLoading) {
        LoadingIndicator.showWithText('', LoadingIndicator.Style.NoIcon);
      }

      Promise.all([
        PlayableMatchesStore.fetchTournamentCategories(true),
        PlayableMatchesStore.fetchMatchesAndProfile(true),
      ])
        .then(([response]) => {
          const categories = [];

          if (response.rows) {
            for (const index in response.rows) {
              categories.push(response.rows[index]);
            }
          } else {
            for (const index in response) {
              categories.push(response[index]);
            }
          }
          this._initializeTournaments(categories);

          if (categories && categories.length > 0) {
            this._finishTournamentFetch(categories, isFTUE);
            this._resetRetryState(); // Success - reset retry counter
          } else {
            // Empty response - log warning
            DdLogs.warn('[TOURNAMENT_FETCH_WARNING] fetchMatchList returned empty categories', {
              responseType: typeof response,
              hasRows: !!response?.rows,
              appState: AppState.currentState,
              timestamp: new Date().toISOString(),
            });
          }
        })
        .catch((error) => {
          this._isFetching = false;
          if (showLoading) {
            LoadingIndicator.hide();
          }

          this._logTournamentError(error, 'fetchMatchList', {
            useCache: true,
            showLoading,
            isFTUE,
          });

          // Retry logic with exponential backoff
          if (this._fetchRetryCount < this._maxRetries) {
            this._fetchRetryCount++;
            setTimeout(() => {
              this.fetchMatchList(showLoading, isFTUE);
            }, 1000 * this._fetchRetryCount);
          } else {
            // Show error dialog after exhausting retries
            DdLogs.warn('[TOURNAMENT_FETCH_FAILED] All retries exhausted for fetchMatchList', {
              retryCount: this._fetchRetryCount,
              maxRetries: this._maxRetries,
              appState: AppState.currentState,
              timestamp: new Date().toISOString(),
            });

            // Mark pending retry so we'll retry when user returns to home screen
            this._markPendingRetryOnInteract();

            this._showTournamentFetchErrorDialog(() => {
              this._fetchRetryCount = 0;
              this._pendingRetryOnInteract = false; // User manually retried, clear pending
              this.fetchMatchList(showLoading, isFTUE);
            });
          }
        });

      if (!ClientPropertyConstants.isCoreLoopTungstenEnabled()) {
        this._refreshTournaments(showLoading, isFTUE);
      }
    }
  },

  _initializeTournaments(categories) {
    categories.forEach((category) => {
      if (category.tournaments instanceof Array) {
        category.tournaments.forEach((obj, idx) => {
          const tournamentObj = new Tournament(obj);
          category.tournaments[idx] = tournamentObj;
        });
      }
      if (category.type == SYNC && category.tournament_template_sets instanceof Array) {
        GlobalEventEmitter.emit(
          SKZConstants.INIT_STAKES_SELECTOR,
          category.tournament_template_sets
        );
      }
    });
  },

  async _refreshTournaments(showLoading = false, isFTUE = false) {
    try {
      const [response]: [SKZTournamentCategoryRecord[]] = await Promise.all([
        PlayableMatchesStore.fetchTournamentCategories(false),
        PlayableMatchesStore.fetchMatchesAndProfile(true),
      ]);

      if (!response || (Array.isArray(response) && response.length === 0)) {
        DdLogs.warn('[TOURNAMENT_FETCH_WARNING] _refreshTournaments returned empty response', {
          responseType: typeof response,
          isArray: Array.isArray(response),
          appState: AppState.currentState,
          timestamp: new Date().toISOString(),
        });
      }

      this._mergeFriendsTournaments(response, (mergedResponse) => {
        this._mergeLiveEventCreationTemplates(mergedResponse, (fullyMergedResponse) => {
          this._markOnFocusedTemplate(fullyMergedResponse);
          this._finishTournamentFetch(fullyMergedResponse, isFTUE);
          this._resetRetryState(); // Success
        });
      });
      if (showLoading) {
        LoadingIndicator.hide();
      }
    } catch (e) {
      if (showLoading) {
        LoadingIndicator.hide();
      }

      this._logTournamentError(e, '_refreshTournaments', {
        useCache: false,
        showLoading,
        isFTUE,
      });

      // Note: _refreshTournaments is a background refresh, so we don't show error dialog here
      // The primary fetchMatchList handles user-visible errors
    }
  },

  reset() {
    this.clearMatchSelectCache();
    this.clearExpandedCategories();
    this.clearLastFilterUnlocked();
  },

  clearMatchSelectCache() {
    this._data = null;
    this._tournamentCategories = null;
    // Fix: Clear all tournament_categories cache variants (with/without query params)
    RESTStore.clearCachedResponseByPrefix('tournament_categories');
  },

  clearExpandedCategories() {
    AsyncStorage.removeItem(CATEGORY_KEYS, () => this.emitChange());
  },

  clearLastFilterUnlocked() {
    const key = this.getFilterUnlockedKey();
    if (key) Preferences.set({[key]: undefined});
  },

  _mergeLiveEventCreationTemplates(categoriesResponse: Object, completion) {
    if (ClientPropertyConstants.getLiveEventCreationEnabled() && Platform.OS !== 'web') {
      // Immediately set create_live_event_cell to true for LIVE sections
      // This ensures the cell is always present and immediately available
      const liveEventSectionIdx = _.findIndex(categoriesResponse, ['type', 'LIVE']);
      if (liveEventSectionIdx >= 0) {
        categoriesResponse[liveEventSectionIdx].create_live_event_cell = true;
      } else {
        // Create LIVE section if it doesn't exist
        const syncSectionIdx = _.findIndex(categoriesResponse, ['type', 'SYNC']);
        const liveEventSection = {
          type: 'LIVE',
          name: I18n.message('Events'),
          description: '',
          highlight_text: null,
          create_live_event_cell: true,
          tournaments: [],
          tournament_template_sets: null,
        };
        let newResponse = [];
        if (syncSectionIdx >= 0) {
          newResponse = [
            ...categoriesResponse.slice(0, syncSectionIdx + 1),
            liveEventSection,
            ...categoriesResponse.slice(syncSectionIdx + 1),
          ];
        } else {
          newResponse = [liveEventSection, ...categoriesResponse];
        }
        // Update the original array
        categoriesResponse.splice(0, categoriesResponse.length, ...newResponse);
      }

      // Complete immediately with the cell always present
      completion(categoriesResponse);

      // Still fetch templates in background for other purposes (like validation)
      this._fetchLiveEventCreationTemplates()
        .then((response: SKZLiveEventTemplateSet) => {
          // Templates fetched successfully - could be used for additional validation
          console.debug('Live event templates fetched:', response.event_templates?.length || 0);
        })
        .catch((error: unknown) => {
          console.debug('Failed to fetch live event templates:', error);
        });
    } else {
      completion(categoriesResponse);
    }
  },

  _fetchLiveEventCreationTemplates(): Promise {
    return new Promise((resolve: (response: Object) => void, reject: (error: unknown) => void) => {
      RESTStore.getServerResponse(
        'tournament_categories/events/templates',
        null,
        SKZAPIManager.OnError.failSilently,
        (responseCall: Object, errorCall: Object) => {
          if (errorCall) {
            reject(errorCall);
            return;
          }

          const formattedResponse = new SKZLiveEventTemplateSet(responseCall);
          resolve(formattedResponse);
        }
      );
    });
  },

  _mergeFriendsTournaments(categoriesResponse: Object, completion) {
    if (this.isFriendsTournamentsEnabled()) {
      FriendsTournamentApiController.fetchTournamentData()
        .then((response: Object) => {
          const friendsTournaments = _.get(response, 'tournaments');

          _.forEach(categoriesResponse, (category) => {
            if (category.type == FRIENDS && category.tournaments) {
              category.tournaments = friendsTournaments.concat(category.tournaments);
            }
          });

          completion(categoriesResponse);
        })
        .catch((error: unknown) => {
          console.debug(
            "FriendsTournamentTemplateView: Can't fetch Tournament Data: " + JSON.stringify(error)
          );
          completion(categoriesResponse);
        });
    } else {
      completion(categoriesResponse);
    }
  },

  // Marks the tournament templates the SDK should scroll to and animate if the template should display a banner.
  // Priority for the banners follows:
  //   1. Z (Play Your First Match)
  //   2. Cash (Play Your First Cash Match)
  //   3. Free Entry Friends Tournaments
  _markOnFocusedTemplate(tournamentCategories: Array<Object>) {
    const zCategory = _.find(
      tournamentCategories,
      (category) => category.type === Z_TEMPLATE && category.highlight_text != null
    );
    if (zCategory) {
      if (_.get(zCategory, 'tournaments', []).length > 0) {
        _.get(zCategory, 'tournaments[0]').isFocused = true;
      }
      return;
    }
    const cashCategory = _.find(
      tournamentCategories,
      (category) => category.type === CASH && category.highlight_text != null
    );
    if (cashCategory) {
      if (_.get(cashCategory, 'tournaments', []).length > 0) {
        _.get(cashCategory, 'tournaments[0]').isFocused = true;
      }
      return;
    }
    const friendsCategory = _.find(tournamentCategories, (category) => category.type === FRIENDS);
    for (let i = 0; i < friendsCategory?.tournaments?.length; i++) {
      if (_.get(friendsCategory.tournaments[i], 'highlight_text', null)) {
        friendsCategory.tournaments[i].isFocused = true;
        return;
      }
    }
  },

  /**
   * Allow to transform the previously saved array into the new hash (object) to use
   * @param {Array[string]} arr
   */
  _convertLegacyExpandedCategories(arr) {
    const obj = {};
    for (let i = 0; i < arr.length; i += 1) {
      obj[arr[i]] = true;
    }
    return obj;
  },

  _finishTournamentFetch(data: [SKZTournamentCategoryRecord[]], isFTUE: boolean) {
    if (data && data.slice) {
      this._data = data.slice();
      AsyncStorage.getItem(CATEGORY_KEYS).then((value) => {
        let saveValue = {};
        try {
          saveValue = value ? JSON.parse(value) : saveValue;
        } catch {
          saveValue = value ? value.split(',') : saveValue;
        }
        this._expandedCategories = Array.isArray(saveValue)
          ? this._convertLegacyExpandedCategories(saveValue)
          : saveValue;

        if (this._expandedCategories[Z_TEMPLATE] === undefined) {
          this._expandedCategories[Z_TEMPLATE] = this.getCategoryExpanded(Z_TEMPLATE, isFTUE);
        }

        if (this._expandedCategories[CASH] === undefined || isFTUE) {
          this._expandedCategories[CASH] = this.getCategoryExpanded(CASH, isFTUE);
        }

        if (this._expandedCategories['RECOMMENDEDV2'] === undefined) {
          this._expandedCategories['RECOMMENDEDV2'] = true;
        }
        this._isFetching = false;
        this.emitChange();
      });
    }
  },

  async fetchTournamentCategories(useCache: boolean = true) {
    const fetchStartTime = Date.now();

    return new Promise((resolve, reject) => {
      const handleResponse = (response: SKZTournamentCategoryRecord[], error) => {
        const fetchDuration = Date.now() - fetchStartTime;
        const currentAppState = AppState.currentState;

        // Helper to log error with native state - all fields flattened to avoid [object Object]
        const logErrorWithNativeState = (
          tag: string,
          errorObj: Error | unknown,
          extraInfo: Record<string, unknown>
        ) => {
          const flattenedInfo = {
            ...extraInfo,
            appState: currentAppState,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            // Flatten error fields using robust error extraction
            errorMessage: getErrorMessage(errorObj),
            errorCode: (errorObj as any)?.code ?? null,
            errorStatusCode: (errorObj as any)?.statusCode ?? (errorObj as any)?.status ?? null,
          };

          if (Platform.OS === 'ios' && SkillzModule?.shouldInteractWithInterface) {
            SkillzModule.shouldInteractWithInterface(
              (shouldInteract: boolean, isMatchInProgress: boolean) => {
                DdLogs.warn(tag, {
                  ...flattenedInfo,
                  shouldInteractWithInterface: shouldInteract,
                  isMatchInProgress,
                });
              }
            );
          } else {
            DdLogs.warn(tag, {
              ...flattenedInfo,
              shouldInteractWithInterface: null,
              isMatchInProgress: null,
            });
          }
        };

        // Check for error case first
        if (error) {
          logErrorWithNativeState(
            '[TOURNAMENT_FETCH_ERROR] fetchTournamentCategories failed',
            error,
            {
              useCache,
              fetchDurationMs: fetchDuration,
            }
          );
          reject(error);
          return;
        }

        // Check for WAF/HTML response (indicates blocked request)
        if (typeof response === 'string') {
          const wafError = new Error('WAF_BLOCK_DETECTED');
          logErrorWithNativeState(
            '[TOURNAMENT_FETCH_ERROR] WAF BLOCK DETECTED - Response is HTML/string',
            wafError,
            {
              responsePreview: (response as string).slice(0, 200),
              useCache,
              fetchDurationMs: fetchDuration,
            }
          );
          reject(wafError);
          return;
        }

        // Check for null/undefined response
        if (!response) {
          const nullError = new Error('NULL_RESPONSE');
          logErrorWithNativeState(
            '[TOURNAMENT_FETCH_ERROR] Null/undefined response received',
            nullError,
            {
              responseType: typeof response,
              useCache,
              fetchDurationMs: fetchDuration,
            }
          );
          reject(nullError);
          return;
        }

        if (response instanceof Array) {
          if (!this.isFriendsTournamentsEnabled()) {
            response = response.filter((item) => item.type !== FRIENDS);
          }
          response.forEach((category) => {
            if (category.tournaments instanceof Array) {
              const tournaments = category.tournaments.map((obj: Object) => new Tournament(obj));
              const isAnyTouramentFeatured = tournaments.some((t) => t.featured);
              if (!isAnyTouramentFeatured && tournaments.length > 0) {
                tournaments[0].featured = true;
              }
              category.tournaments = tournaments;
            }

            if (
              category.type == SYNC &&
              category.tournament_template_sets instanceof Array &&
              Platform.OS !== 'web'
            ) {
              GlobalEventEmitter.emit(
                SKZConstants.INIT_STAKES_SELECTOR,
                category.tournament_template_sets
              );
            }
          });

          // Filter H2H async templates based on max eligible entry fee ceiling
          if (ClientPropertyConstants.isH2HMaxEligibleEntryFeeEnabled()) {
            const maxEntryFeeCeiling = ClientPropertyConstants.getH2HMaxEligibleEntryFeeCeiling();
            response.forEach((category) => {
              if (category.type === CASH && category.tournaments instanceof Array) {
                category.tournaments = category.tournaments.filter((tournament) => {
                  // Only filter async (H2H) tournaments - synchronous === false means async
                  if (
                    tournament.synchronous === false &&
                    tournament.cash_fee > maxEntryFeeCeiling
                  ) {
                    return false;
                  }
                  return true;
                });
              }
            });
          }

          // On Web, hide Ad based live events as there is no ad integration for Web
          if (Platform.OS === 'web') {
            response.forEach((category) => {
              if (category.type === LIVE && category.tournaments instanceof Array) {
                category.tournaments = category.tournaments.filter(
                  (tournament) => tournament.video_ad_entry_enabled !== true
                );
              }
            });
          }

          if (response && response.length > 0 && !this._liveActivityStarted) {
            this._liveActivityStarted = true;
            this.startTournamentLiveActivity(response);
          }

          resolve(response);
        } else {
          // Response is not an array - unexpected format
          logErrorWithNativeState('[TOURNAMENT_FETCH_ERROR] Response is not an array', error, {
            responseType: typeof response,
            responseKeys: response ? Object.keys(response).join(',') : null,
            useCache,
            fetchDurationMs: fetchDuration,
          });
          reject(error || new Error('INVALID_RESPONSE_FORMAT'));
        }
      };

      // Dynamically get user path selection from FTUEController
      const FTUEController = require('FTUEController').default;
      const userPathSelection = FTUEController.getUserPathSelection();

      // Build API URL: only add query parameter if user has made a selection
      let apiUrl = 'tournament_categories/';
      if (userPathSelection) {
        const userPath = userPathSelection.toLowerCase();
        apiUrl = `tournament_categories/?new_user_path=${userPath}`;
      }

      // Use failSilently when suppress_uhoh_dialog flag is enabled AND app is within background resume grace period
      // This avoids popups on background refresh while still showing errors during normal user interactions
      const shouldFailSilently =
        ClientPropertyConstants.getSuppressUhOhDialog() && isWithinBackgroundResumeGracePeriod();
      const errorHandleType = shouldFailSilently
        ? SKZAPIManager.OnError.failSilently
        : SKZAPIManager.OnError.promptRetry;

      if (useCache) {
        RESTStore.getAnyResponse(apiUrl, null, errorHandleType, handleResponse);
      } else {
        RESTStore.getServerResponse(apiUrl, null, errorHandleType, handleResponse);
      }
    });
  },
  startTournamentLiveActivity(response: SKZTournamentCategoryRecord[]) {
    const liveEventSection = response.find((category) => category.type === 'LIVE');

    if (!liveEventSection || !Array.isArray(liveEventSection.tournaments)) {
      return;
    }

    const tournaments = liveEventSection.tournaments.filter(
      (tournament: TournamentRecord) => !this._shouldFilterExpiredLiveEvent(tournament)
    );

    if (!tournaments.length) {
      return;
    }

    // Prefer a free entry tournament if available, otherwise use the first tournament
    const freeEntryTournament = tournaments.find((t) => t.cash_fee === 0);
    const tournamentToStart: TournamentRecord = freeEntryTournament || tournaments[0];
    if (
      tournamentToStart &&
      (tournamentToStart.isCashTournament() ||
        (tournamentToStart.isCustomPrizeTournament() &&
          (tournamentToStart.estimated_total_value ?? 0) > 0))
    ) {
      // LiveActivityManager.startLiveEventLiveActivity(tournamentToStart);
    }
  },
  async fetchMatchesAndProfile(useCache = true) {
    if (
      !ClientPropertyConstants.getMatchTemplateFTUEEnabled() ||
      this.getLastFilterUnlocked() === NewUserMatchFilter.NONE
    ) {
      // Skip fetching matches and profile if we're not doing the match template
      // FTUE or we've already unlocked all match types (no more need to check
      // conditions)
      return;
    }

    const fetchMatchesPromise = new Promise((resolve, reject) => {
      const handleResponse = (response, error) => {
        if (response instanceof Array) {
          this._matches = response;
          resolve(response);
        } else {
          reject(error);
        }
      };

      CurrentUserStore.getUserData(true)
        .then((user: Object) => {
          const userId = _.get(user, 'id');
          if (!userId) {
            return reject(new Error('User ID not found'));
          }

          this._userId = userId;
          const zBalance = _.get(user, 'z_balance', 0);
          this._zBalance = zBalance ?? this._zBalance;

          // Use failSilently when suppress_uhoh_dialog flag is enabled AND app is within background resume grace period
          const shouldFailSilently =
            ClientPropertyConstants.getSuppressUhOhDialog() && isWithinBackgroundResumeGracePeriod();
          const errorHandleType = shouldFailSilently
            ? SKZAPIManager.OnError.failSilently
            : SKZAPIManager.OnError.promptRetry;

          if (useCache) {
            RESTStore.getAnyResponse(
              'users/' + userId + '/matches',
              null,
              errorHandleType,
              handleResponse
            );
          } else {
            RESTStore.getServerResponse(
              'users/' + userId + '/matches',
              null,
              errorHandleType,
              handleResponse
            );
          }
        })
        .catch((error: unknown) => {
          reject(error);
        });
    });

    const fetchProfilePromise = CurrentUserStore.fetchUserProfile(true).then((profile) => {
      this._totalGamesPlayed = profile.total_games_played || 0;
      this._joinDate = moment(profile.created_at);
    });

    await Promise.all([fetchMatchesPromise, fetchProfilePromise]);
    this._newUserMatchFilter = getNewUserMatchFilter(this._matches, this._totalGamesPlayed);
    this.processNewUserMatchFilter();
  },

  getNewUserMatchFilter() {
    return this._newUserMatchFilter;
  },

  getFilterUnlockedKey() {
    return this._userId ? `LastNewUserMatchFilterUnlocked_${this._userId}` : undefined;
  },

  setFilterUnlocked(filter = this._newUserMatchFilter) {
    const key = this.getFilterUnlockedKey();
    if (key) Preferences.set({[key]: filter});
  },

  getLastFilterUnlocked() {
    const key = this.getFilterUnlockedKey();
    return key ? Preferences.get(key) : undefined;
  },

  unlockedAllFilters() {
    return this.getLastFilterUnlocked() === NewUserMatchFilter.NONE;
  },

  shouldShowUnlockedNotification(filter = this._newUserMatchFilter) {
    switch (filter) {
      case NewUserMatchFilter.ALL_PRO_ENTRY_FEES:
      case NewUserMatchFilter.SPECIAL_MODES:
      case NewUserMatchFilter.NONE:
        // We only show notifications when unlocking all pro EFs (special modes
        // now unlocked) and when unlocking everything
        return this.unlockedNewMatchFilter(filter);
      default:
        return false;
    }
  },

  unlockedNewMatchFilter(filter = this._newUserMatchFilter) {
    const lastUnlockedFilter = this.getLastFilterUnlocked();
    switch (filter) {
      case NewUserMatchFilter.EXPAND_Z:
        return !lastUnlockedFilter;
      case NewUserMatchFilter.EXPAND_PRO:
        return lastUnlockedFilter === NewUserMatchFilter.EXPAND_Z;
      case NewUserMatchFilter.ALL_PRO_ENTRY_FEES:
        // All pro entry fees are unlocked when the user has played 3 cash games,
        // so they must have already unlocked the expand pro filter (unlocked at
        // 1 cash game)
        return lastUnlockedFilter === NewUserMatchFilter.EXPAND_PRO;
      case NewUserMatchFilter.SPECIAL_MODES:
        // Special modes are unlocked when the user has played any 10 games, so
        // both these previous filters are possible
        return [NewUserMatchFilter.EXPAND_PRO, NewUserMatchFilter.ALL_PRO_ENTRY_FEES].includes(
          lastUnlockedFilter
        );
      case NewUserMatchFilter.NONE:
        return lastUnlockedFilter === NewUserMatchFilter.SPECIAL_MODES;
      default:
        return false;
    }
  },

  processNewUserMatchFilter() {
    // If we don't have a stored value for the last filter we've unlocked,
    // initialize it so we can show notifications for newly unlocked filters
    if (!this.getLastFilterUnlocked()) {
      this.setFilterUnlocked();
      return;
    }

    // If we haven't unlocked a new filter or we've unlocked all matches
    // (NewUserMatchFilter.NONE), we don't need to do anything
    if (!this.unlockedNewMatchFilter() || this._newUserMatchFilter === NewUserMatchFilter.NONE) {
      return;
    }

    // Override the category expanded state for the unlocked filter if applicable;
    // this ensures that if the user changed the expanded state manually, we
    // still expand the relevant categories when they unlock new matches
    this._expandedCategories[Z_TEMPLATE] =
      this.getCategoryExpanded(Z_TEMPLATE) ?? this._expandedCategories[Z_TEMPLATE];
    this._expandedCategories[CASH] =
      this.getCategoryExpanded(CASH) ?? this._expandedCategories[CASH];
    this.saveExpandedCategories();
  },

  getMatchSectionFilterFunction(): MatchSectionFilterFunction | undefined {
    // 🔥 FIX: RP-6891 - FTUE tooltip appears after expanding Practice section
    if (ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
      // Ensure Practice section is always expanded for FTUE tooltips
      if (!this.isCategoryExpanded('Z')) {
        this._expandedCategories['Z'] = true;
        this.saveExpandedCategories();
      }

      return INCLUDE_ALL_SECTIONS;
    }

    switch (this._newUserMatchFilter) {
      case NewUserMatchFilter.EXPAND_Z:
      case NewUserMatchFilter.EXPAND_PRO:
      case NewUserMatchFilter.ALL_PRO_ENTRY_FEES:
      case NewUserMatchFilter.SPECIAL_MODES:
        // We only show Z and cash sections until the user has unlocked brackets,
        // i.e. all other game modes (NewUserMatchFilter.NONE below)
        return ({type}: MatchSection) => ['Z', 'CASH'].includes(type);
      case NewUserMatchFilter.NONE:
        return INCLUDE_ALL_SECTIONS;
      default:
        return;
    }
  },

  updateDataToHighlightMatch(payload) {
    if (payload && payload.id) {
      this._shouldDisplaySearchResults = false;
      this._highlightedMatch = payload.id;
      this._highlightedMatchText = payload.text;
      let categoryType = null;
      this._data.some((category, index, array) => {
        category.tournaments.some((tournament, sectionIndex, sectionArray) => {
          if (tournament.id == payload.id) {
            categoryType = category.type;
            return true;
          } else {
            return false;
          }
        });
      });

      // 🔥 FIX: RP-6911 - "View More" navigates to correct highlighted section
      if (ClientPropertyConstants.getFTUEV2FeatureEnabled() && categoryType) {
        // Clear any previous highlights to prevent wrong navigation
        Object.keys(this._expandedCategories).forEach((key) => {
          if (key !== categoryType && this._expandedCategories[key] === 'highlighted') {
            delete this._expandedCategories[key];
          }
        });

        // Mark the correct category as highlighted
        this._expandedCategories[categoryType] = true;
        this._expandedCategories[`${categoryType}_highlighted`] = true;
      } else {
        //Ensure it's category is expanded
        if (!this.isCategoryExpanded(categoryType)) {
          this._expandedCategories[categoryType] = true;
        }
      }

      this.emitChange();
    }
  },

  selectMatchCategory(matchCategoryType: string) {
    if (this.isCategoryExpanded(matchCategoryType) && this._data) {
      AWSManager.trackEvent(MATCH_SELECT_COLLAPSE_SECTION, {category: matchCategoryType});

      let searchCategoryType = null;
      this._data.some((category, index, array) => {
        if (_.get(category, 'tournaments[0].live_event')) {
          searchCategoryType = category.type;
          return true;
        } else {
          return false;
        }
      });

      if (matchCategoryType === searchCategoryType) {
        this._shouldDisplaySearchResults = false;
      }

      this._expandedCategories[matchCategoryType] = false;
    } else {
      AWSManager.trackEvent(MATCH_SELECT_OPEN_SECTION, {category: matchCategoryType});
      this._expandedCategories[matchCategoryType] = true;
    }
    const awsData = JSON.stringify({
      section_name: matchCategoryType,
      status_expanded: this.isCategoryExpanded(matchCategoryType),
    });
    AWSManager.trackEvent(SECTION_TEMPLATE_EXPAND_VIEW_CLICK, {result: awsData});
    this.saveExpandedCategories();
  },

  async saveExpandedCategories() {
    await AsyncStorage.setItem(CATEGORY_KEYS, JSON.stringify(this._expandedCategories));

    await AsyncStorage.getItem(CATEGORY_KEYS).then((value) => {
      this.emitChange();
    });
  },

  searchForEvents(searchTerm) {
    if (searchTerm.length) {
      this._shouldDisplaySearchResults = true;
      if (Platform.OS == 'android') {
        LoadingIndicator.show();
        RESTStore.getServerResponse(
          'tournaments/',
          {search: searchTerm},
          true,
          (payload, error) => {
            LoadingIndicator.hide();
            this._searchResults = payload;
            this.emitChange();
          }
        );
      } else {
        LoadingIndicator.showWithText('', LoadingIndicator.Style.NoIcon);
        RESTStore.getServerResponse(
          'tournaments/',
          {search: searchTerm},
          SKZAPIManager.OnError.promptRetry,
          (payload, error) => {
            LoadingIndicator.hide();
            this._searchResults = payload;
            this.emitChange();
          }
        );
      }
    } else {
      this._shouldDisplaySearchResults = false;
      this.emitChange();
    }
  },

  getSectionsData(shouldFilterToZTemplate: boolean, shouldFilterToOneTemplateSet: boolean = false) {
    const dataBlob: MatchSelectListSection[] = [];
    if (this._data instanceof Array) {
      _.forEach(this._data, (category, index) => {
        this._insertSingleTemplates(category, dataBlob, shouldFilterToZTemplate);
        this._insertTemplateSets(
          category,
          dataBlob,
          shouldFilterToZTemplate,
          shouldFilterToOneTemplateSet
        );
        this._insertLiveEventCreationCell(category, dataBlob, shouldFilterToZTemplate);
      });
    }
    this.lastSectionType = _.get(dataBlob[dataBlob.length - 1], 'type');
    this._insertEventSectionForMatchCode(dataBlob);
    if (ClientPropertyConstants.getRecommendedTemplatesV2UIenabled()) {
      this._insertRecommendedTemplateV2(dataBlob);
    } else {
      this._insertRecommendedTemplate(dataBlob);
    }

    // Ensure each section has at least one featured tournament if it has any tournaments
    this._ensureSectionsHaveFeaturedTournaments(dataBlob);

    // Log warning if all sections empty (production issue indicator)
    if (dataBlob.length === 0 || dataBlob.every((s) => !s.data?.length)) {
      DdLogs.warn('[TOURNAMENT_SECTIONS_EMPTY] All sections empty after filtering', {
        hasRawData: this._data instanceof Array,
        rawDataLength: this._data?.length || 0,
        appState: AppState.currentState,
        platform: Platform.OS,
        timestamp: new Date().toISOString(),
      });
    }

    return dataBlob;
  },

  _insertEventSectionForMatchCode(dataBlob: Array<MatchSelectListSection>) {
    if (!ClientPropertyConstants.getMatchCodeVisibilityEnabled()) {
      return;
    }

    const liveEventSection = _.find(dataBlob, {type: 'LIVE'});
    if (!liveEventSection) {
      dataBlob.unshift(
        new MatchSelectListSection({
          type: 'LIVE',
          name: 'Events',
          description: '',
          length: 0,
          data: [],
        })
      );
    }
  },

  _insertLiveEventCreationCell(
    category: Object,
    dataBlob: Array<MatchSelectListSection>,
    shouldFilterToZTemplate: boolean
  ) {
    if (!(category?.type === 'LIVE' && !shouldFilterToZTemplate)) {
      return;
    }

    const section = this._getSectionListForCategory(dataBlob, category);

    // Always show the creation cell if live event creation is enabled
    // This ensures the cell is always present and immediately available
    const shouldShowCreateCell =
      ClientPropertyConstants.getLiveEventCreationEnabled() && Platform.OS !== 'web';

    if (shouldShowCreateCell) {
      // Always add the create live event cell to the LIVE section
      section.data?.unshift({create_live_event_cell: true});
    }
  },

  /**
   * Function returns the player's last played cash tournament or the cheapest cash tournament.
   * @param cashTournaments: All the available  cash tournaments.
   * @returns The last played cash tournament or the cheapest cash tournament.
   */
  getLastPlayedCashTournament(cashTournaments: Tournament[]): Tournament | undefined {
    let lastPlayedCashTournament: Tournament | undefined = undefined;
    try {
      if (Preferences.get(SKZConstants.LAST_PLAYED_CASH_TOURNAMENT)) {
        lastPlayedCashTournament = new Tournament(
          JSON.parse(Preferences.get(SKZConstants.LAST_PLAYED_CASH_TOURNAMENT))
        );
      }
    } catch (error) {
      console.debug('PlayableMatchesStore: failed to parse last played cash tournament:  ' + error);
    }

    if (
      lastPlayedCashTournament &&
      cashTournaments.some(
        (tournament) => String(tournament.id) === String(lastPlayedCashTournament.id)
      )
    ) {
      // DISCOUNTED MERGE: Get the actual tournament from available list and merge with stored tournament to preserve discount values
      const actualTournament = cashTournaments.find(
        (tournament) => String(tournament.id) === String(lastPlayedCashTournament.id)
      );
      if (actualTournament) {
        return {
          ...actualTournament, // Override with current availability data
          id: actualTournament.id, // Keep the current tournament's ID
          cash_fee: actualTournament.cash_fee, // Keep the current tournament's fee
          cash_fee_discount: actualTournament.cash_fee_discount,
        };
      }
      return actualTournament;
    } else {
      let cheapestCashTournament = cashTournaments.reduce((cheapest, current) =>
        current.cash_fee < cheapest.cash_fee ? current : cheapest
      );

      // If we have a last played cash tournament but it's not available, merge its properties with the cheapest one
      if (lastPlayedCashTournament && cheapestCashTournament) {
        cheapestCashTournament = {
          ...cheapestCashTournament,
          id: cheapestCashTournament.id, // Keep the current tournament's ID
          cash_fee: cheapestCashTournament.cash_fee, // Keep the current tournament's fee
          cash_fee_discount: cheapestCashTournament.cash_fee_discount,
        };
      }

      return cheapestCashTournament;
    }
  },

  /**
   * Function returns the player's last played live event tournament.
   * @returns The last played live event tournament or undefined.
   */
  getLastLiveEventPlayed(): Tournament | undefined {
    let lastPlayedLiveEventTournament: Tournament | undefined = undefined;
    try {
      if (Preferences.get(SKZConstants.LAST_PLAYED_LIVE_EVENT)) {
        lastPlayedLiveEventTournament = new Tournament(
          JSON.parse(Preferences.get(SKZConstants.LAST_PLAYED_LIVE_EVENT))
        );
      }
    } catch (error) {
      console.debug(
        'PlayableMatchesStore: failed to parse last played live event tournament:  ' + error
      );
    }
    return lastPlayedLiveEventTournament;
  },

  /**
   * Function returns the closest available cash tournament to the target fee.
   * @param targetFee: the recommended tournament fee.
   * @param availableFees: All the available fees for the cash tournaments.
   * @returns the closest available fee to the target fee.
   */
  getClosestAvialableCashTournament(targetFee: number, availableFees: number[]): number {
    return availableFees.reduce(
      (closest, current) =>
        Math.abs(current - targetFee) < Math.abs(closest - targetFee) ? current : closest,
      availableFees[0]
    );
  },

  /**
   * Function retrieves a cash tournament based on a specific recommendation type
   * @param recommendation: The recommendation condition set on AP.
   * @param cashTournaments: All the cash tournaments available for the user.
   * @param sortedFees: All the available fees for the cash tournaments.
   * @param recommendedMatchEntryFee: The recommended match entry fee.
   * @returns: The cash tournament based on the recommendation.
   */
  getCashTournamentByRecommendation(
    recommendation: SKZConstants.RecommendedTemplateConditions,
    cashTournaments: Tournament[],
    sortedFees: number[],
    recommendedMatchEntryFee: number
  ): Tournament | undefined {
    if (cashTournaments.length === 0) return undefined;

    // Get the most played fee from sortedFees, or fallback to closest fee
    const mostPlayedFee = sortedFees.includes(recommendedMatchEntryFee)
      ? recommendedMatchEntryFee
      : undefined;

    const closestFee = this.getClosestAvialableCashTournament(recommendedMatchEntryFee, sortedFees);

    // If the most played entry fee is not available, use the closest entry fee available.
    const recommendedFee = mostPlayedFee ?? closestFee;

    // Get upsell fees
    const upsellLowFee = sortedFees.find((fee) => fee > recommendedFee);
    const upsellMediumFee = upsellLowFee ? sortedFees.find((fee) => fee > upsellLowFee) : undefined;
    const upsellHighFee = upsellMediumFee
      ? sortedFees.find((fee) => fee > upsellMediumFee)
      : undefined;

    // Select the appropriate fee based on the recommendation condition.
    let selectedFee;
    switch (recommendation) {
      case SKZConstants.RecommendedTemplateConditions.MOST_PLAYED:
        selectedFee = recommendedFee;
        break;
      case SKZConstants.RecommendedTemplateConditions.UPSELL_LOW:
        selectedFee = upsellLowFee || recommendedFee;
        break;
      case SKZConstants.RecommendedTemplateConditions.UPSELL_MEDIUM:
        selectedFee = upsellMediumFee || upsellLowFee || recommendedFee;
        break;
      case SKZConstants.RecommendedTemplateConditions.UPSELL_HIGH:
        selectedFee = upsellHighFee || upsellMediumFee || upsellLowFee || recommendedFee;
        break;
      case SKZConstants.RecommendedTemplateConditions.LAST_PLAYED:
        // Return the last played cash tournament if available. Else return the cheapest cash tournament.
        return this.getLastPlayedCashTournament(cashTournaments);
      case SKZConstants.RecommendedTemplateConditions.LIVE_EVENT:
        // Return the last played live event tournament if available. Else return the cheapest live event tournament.
        return this.getLastLiveEventPlayed();
      default:
        // Return if the recommendation is NONE.
        return undefined;
    }

    // Return the first matching tournament based on the selected fee.
    const foundTournament = cashTournaments.find((t) => t.cash_fee === selectedFee);

    // If we found a tournament and have last played data, merge to preserve updated props from API response
    if (
      foundTournament &&
      recommendation === SKZConstants.RecommendedTemplateConditions.LAST_PLAYED
    ) {
      const lastPlayedCashTournament = this.getLastPlayedCashTournament(cashTournaments);
      if (lastPlayedCashTournament && lastPlayedCashTournament.id !== foundTournament.id) {
        return {
          ...lastPlayedCashTournament,
          ...foundTournament,
          id: foundTournament.id, // Keep the current tournament's ID
          cash_fee: foundTournament.cash_fee, // Keep the current tournament's fee
        };
      }
    }

    return foundTournament;
  },

  /**
   * Function to get the recommended cash tournaments based on the recommendation conditions.
   * Ensures that duplicate tournaments are not added to the recommendations list.
   * @param cashTournaments: All the cash tournaments available for the user.
   * @returns: The recommended cash tournaments and the total recommended tournament count.
   */
  _getRecommendedTemplatesTournaments(cashTournaments: Tournament[]): {
    recommendedTournaments: Tournament[];
    count: number;
  } {
    if (cashTournaments.length === 0) return {recommendedTournaments: [], count: 0};

    // Sort the cash tournaments by fee and store the sorted fees.
    const sortedFees = cashTournaments.map((t) => t.cash_fee).sort((a, b) => a - b);

    // Get the recommended tournament entry fee from client property.
    const recommendedMatchEntryFee = parseFloat(
      ClientPropertyConstants.getRecommendedTemplateEntryFeeValue().replace('$', '')
    );

    let recommendedTournaments = [];
    //Counter to check how many tournaments are recommended. This will be used to configure the accordion.
    let recommendedTournamentCount = 0;
    let recommendedTournamentIds = new Set<string>();

    const addTournament = (
      slotBadgeActive: boolean,
      slotRecommendation: string,
      slotText: string,
      isSlotAlwaysIncluded: boolean,
      slotId: number
    ) => {
      if (!slotBadgeActive || !slotRecommendation) return;

      const slotTournament: Tournament = {
        ...this.getCashTournamentByRecommendation(
          slotRecommendation,
          cashTournaments,
          sortedFees,
          recommendedMatchEntryFee
        ),
      } as Tournament;
      slotTournament.highlight_text = slotText !== 'NA' ? slotText : '';
      slotTournament.category_source = 'recommended_slot' + slotId;
      // If the tournament isn't already added and can be shown on the UI ., i.e the category is expanded,  add the tournament.
      if (!recommendedTournamentIds.has(slotTournament.id)) {
        recommendedTournamentCount++; // Increment count for each valid tournament processed
        if (isSlotAlwaysIncluded || this.isCategoryExpanded('RECOMMENDED')) {
          recommendedTournamentIds.add(slotTournament.id);
          recommendedTournaments.push(slotTournament);
        }
      }
    };

    // Add Slot 1 Tournament (always included)
    addTournament(
      ClientPropertyConstants.getRecommendedSlot1BadgeActive(),
      ClientPropertyConstants.getRecommendedSlot1Recommendation(),
      ClientPropertyConstants.getRecommendedSlot1BadgeText(),
      true,
      1
    );
    // Add Slot 2 Tournament (only if the category is expanded)
    addTournament(
      ClientPropertyConstants.getRecommendedSlot2BadgeActive(),
      ClientPropertyConstants.getRecommendedSlot2Recommendation(),
      ClientPropertyConstants.getRecommendedSlot2BadgeText(),
      false,
      2
    );
    // Add Slot 3 Tournament (only if the category is expanded)
    addTournament(
      ClientPropertyConstants.getRecommendedSlot3BadgeActive(),
      ClientPropertyConstants.getRecommendedSlot3Recommendation(),
      ClientPropertyConstants.getRecommendedSlot3BadgeText(),
      false,
      3
    );

    // Add the cheapest cash tournament/last played tournament if no recommended tournaments are available
    if (recommendedTournaments.length == 0) {
      recommendedTournamentCount++;
      recommendedTournaments.push(this.getLastPlayedCashTournament(cashTournaments));
    }
    return {recommendedTournaments, count: recommendedTournamentCount};
  },

  _insertRecommendedTemplate(dataBlob: Array<MatchSelectListSection>) {
    //Return early if the recommended templates are disabled or the user has not played any games.
    if (!ClientPropertyConstants.getRecommendedTemplatesEnabled() || this.total_games_played <= 0) {
      return;
    }

    let syncTemplates = Array.isArray(this._data)
      ? this._data.find((category: SKZTournamentCategoryRecord) => category.type === SYNC)
      : undefined;
    if (syncTemplates?.tournament_template_sets?.length > 0) {
      return;
    }

    let cashTemplates: SKZTournamentCategoryRecord = Array.isArray(this._data)
      ? this._data.find((category: SKZTournamentCategoryRecord) => category.type === CASH)
      : undefined;

    //Remove any special mode/limited time tournaments from the cashTemplates ensuring we don't modify the original cash templates.
    if (cashTemplates && Array.isArray(cashTemplates.tournaments)) {
      cashTemplates = {
        ...cashTemplates,
        tournaments: (cashTemplates.tournaments || []).filter(
          (tournament) => tournament.limited_use === null
        ),
      };
    }

    //Return early if there are no cash tournaments available.
    if (!cashTemplates?.tournaments?.length) return;

    let {recommendedTournaments, count} = this._getRecommendedTemplatesTournaments(
      cashTemplates.tournaments
    );

    let recommendedSection = new MatchSelectListSection({
      type: 'RECOMMENDED',
      name: 'Recommended',
      description: 'Recommended match section for the user',
      length: count,
      data: recommendedTournaments,
    });
    recommendedSection.configureAccordionForRecommendedTemplate(count);
    dataBlob.unshift(recommendedSection);
  },

  _insertRecommendedTemplateV2(dataBlob: Array<MatchSelectListSection>) {
    const isAscending = ClientPropertyConstants.getRecommendedTemplateV2AscendingEnabled();
    // Assume this boolean is provided or calculated elsewhere
    const isGroup1 = !ClientPropertyConstants.getRecommendedTemplateV2LiveEventEnabled(); // Flag for whether to show live event

    //Return early if the recommended templates are disabled
    if (!ClientPropertyConstants.getRecommendedTemplatesV2UIenabled()) {
      return;
    }

    //Return early if there are sync templates available.
    let syncTemplates = Array.isArray(this._data)
      ? this._data.find((category: SKZTournamentCategoryRecord) => category.type === SYNC)
      : undefined;
    if (syncTemplates?.tournament_template_sets?.length > 0) {
      return;
    }

    // Get all available cash tournaments and live events
    let cashTemplates: SKZTournamentCategoryRecord = Array.isArray(this._data)
      ? this._data.find((category: SKZTournamentCategoryRecord) => category.type === CASH)
      : undefined;
    const liveTemplates: SKZTournamentCategoryRecord = Array.isArray(this._data)
      ? this._data.find((category: SKZTournamentCategoryRecord) => category.type === LIVE)
      : undefined;

    if (!cashTemplates?.tournaments?.length) return;

    // Use ascending order for all selection
    const sortedTournaments = [...cashTemplates.tournaments].sort(
      (a, b) => a.cash_fee - b.cash_fee
    );
    const sortedFees = sortedTournaments.map((t) => t.cash_fee);

    // Most played fee (assume this is calculated elsewhere and available)
    let mostPlayedFee = parseFloat(
      ClientPropertyConstants.getRecommendedTemplateV2EntryFeeValue().replace('$', '')
    );

    // Find the closest available fee if mostPlayedFee is not in sortedFees
    if (!sortedFees.includes(mostPlayedFee)) {
      mostPlayedFee = this.getClosestAvialableCashTournament(mostPlayedFee, sortedFees);
    }

    // Last played cash tournament - check if still available, otherwise find nearest
    const lastPlayedCashTournament = this.getLastPlayedCashTournament(sortedTournaments);
    let LPG = lastPlayedCashTournament ? lastPlayedCashTournament.cash_fee : sortedFees[0];

    // Initialize both tournament references:
    // - currentCashTournament: For preserving discounted fee API updates
    // - LPGTournament: For sorting/duplicate logic bugfix
    let currentCashTournament = lastPlayedCashTournament;
    let LPGTournament = lastPlayedCashTournament;

    // If last played cash tournament is not in current available tournaments, find the nearest one
    if (
      lastPlayedCashTournament &&
      !sortedTournaments.some((t) => String(t.id) === String(lastPlayedCashTournament.id))
    ) {
      const nearestCashFee = this.getClosestAvialableCashTournament(
        lastPlayedCashTournament.cash_fee,
        sortedFees
      );
      LPG = nearestCashFee;

      // Find the actual tournament with the nearest fee (not just the fee)
      // For same fees, prioritize the last played tournament structure
      LPGTournament =
        sortedTournaments.find((t) => t.cash_fee === nearestCashFee) || lastPlayedCashTournament;

      // For discounted fees: Merge with last played cash tournament to preserve updated props from API response
      currentCashTournament = sortedTournaments.find((t) => t.cash_fee === nearestCashFee);
      if (currentCashTournament) {
        currentCashTournament = {
          ...lastPlayedCashTournament,
          ...currentCashTournament,
          id: currentCashTournament.id, // Keep the current tournament's ID
          cash_fee: currentCashTournament.cash_fee, // Keep the current tournament's fee
        };
      }
    }

    // Last played live event - check if still available, otherwise find nearest
    const lastPlayedLiveEvent = this.getLastLiveEventPlayed();
    let liveEventCashLastPlayed = lastPlayedLiveEvent ? lastPlayedLiveEvent.cash_fee : undefined;
    const matchingTournament = liveTemplates?.tournaments?.find(
      (t) => t?.id === lastPlayedLiveEvent?.id
    );

    let currentLiveEvent = matchingTournament ? matchingTournament : lastPlayedLiveEvent;

    // If we have both matchingTournament and lastPlayedLiveEvent, merge them to preserve updated props from API response
    if (currentLiveEvent) {
      currentLiveEvent = {
        ...lastPlayedLiveEvent,
        ...currentLiveEvent,
        id: currentLiveEvent.id, // Keep the current tournament's ID
        live_event: matchingTournament?.live_event || lastPlayedLiveEvent?.live_event,
      };
    }

    // If last played live event is not in current available live events, find the nearest one
    if (lastPlayedLiveEvent && liveTemplates?.tournaments?.length) {
      const isStillAvailable = liveTemplates.tournaments.some(
        (t) => t.id === lastPlayedLiveEvent.id
      );
      if (!isStillAvailable) {
        // Find the closest available live event by fee
        const sortedLiveFees = [...liveTemplates.tournaments]
          .sort((a, b) => a.cash_fee - b.cash_fee)
          .map((t) => t.cash_fee);
        const nearestLiveFee = sortedLiveFees.reduce(
          (closest, current) =>
            Math.abs(current - lastPlayedLiveEvent.cash_fee) <
            Math.abs(closest - lastPlayedLiveEvent.cash_fee)
              ? current
              : closest,
          sortedLiveFees[0]
        );
        liveEventCashLastPlayed = nearestLiveFee;
        currentLiveEvent = liveTemplates.tournaments.find((t) => t.cash_fee === nearestLiveFee);
        // Preserve timer functionality for the fallback tournament
        if (currentLiveEvent) {
          currentLiveEvent = {
            ...currentLiveEvent,
            live_event: currentLiveEvent.live_event || lastPlayedLiveEvent?.live_event,
            category_type: currentLiveEvent.category_type || 'LIVE',
          };
        }

        // Preserve updated props from API response for the fallback tournament
        if (currentLiveEvent) {
          currentLiveEvent = {
            ...lastPlayedLiveEvent,
            ...currentLiveEvent,
            id: currentLiveEvent.id, // Keep the current tournament's ID
            live_event: currentLiveEvent.live_event || lastPlayedLiveEvent?.live_event,
          };
        }
      }
    } else if (lastPlayedLiveEvent && !liveTemplates?.tournaments?.length) {
      // No live events available at all, but user had played live events before
      currentLiveEvent = null;
      liveEventCashLastPlayed = undefined;
      console.debug(
        'PlayableMatchesStore: No live events currently available, user previously played live events'
      );
    }

    // Helper to get tournament by fee
    function getTournamentByFee(fee) {
      // If this is the LPG fee and we have a merged currentCashTournament, use that
      if (fee === LPG && currentCashTournament && currentCashTournament.cash_fee === fee) {
        return currentCashTournament;
      }
      return sortedTournaments.find((t) => t.cash_fee === fee);
    }

    // Helper to get the specific LPG tournament (prioritizing exact ID match)
    function getLPGTournament() {
      return LPGTournament;
    }

    // Helper to check if a tournament is currently live
    function isLiveTournament(tournament) {
      if (!tournament || !liveTemplates?.tournaments?.length) return false;
      return liveTemplates.tournaments.some((t) => t.id === tournament.id);
    }

    let templateSlot3, templateSlot2, templateSlot1;
    const mostPlayedIdx = sortedFees.indexOf(mostPlayedFee);
    const LPGIdx = sortedFees.indexOf(LPG);

    // Helper to get safe tournament by index
    function getTournamentByIndex(index) {
      if (index < 0 || index >= sortedFees.length) return null;
      return getTournamentByFee(sortedFees[index]);
    }

    // Determine LPG vs MPG relationship
    let lpgMpgRelation;
    if (LPG === undefined || LPG === null || LPGIdx === -1) {
      lpgMpgRelation = 'LPG_NOT_AVAILABLE'; // New users
    } else if (mostPlayedFee === undefined || mostPlayedFee === null || mostPlayedIdx === -1) {
      lpgMpgRelation = 'MPG_NOT_AVAILABLE';
    } else if (LPG === mostPlayedFee) {
      lpgMpgRelation = 'LPG_EQUALS_MPG';
    } else if (LPG > mostPlayedFee) {
      lpgMpgRelation = 'LPG_GREATER_MPG';
    } else {
      lpgMpgRelation = 'LPG_LESS_MPG';
    }

    if (isGroup1) {
      // Group 1 logic (LE % < 10%)
      switch (lpgMpgRelation) {
        case 'LPG_EQUALS_MPG':
          templateSlot3 = getTournamentByFee(mostPlayedFee);
          if (mostPlayedIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(mostPlayedIdx - 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx - 2);
          } else if (mostPlayedIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx + 2);
          }
          break;

        case 'LPG_GREATER_MPG':
          templateSlot3 = getLPGTournament();
          if (LPGIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(LPGIdx - 1);
            templateSlot1 = getTournamentByIndex(LPGIdx - 2);
          } else if (LPGIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = getTournamentByIndex(LPGIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = getTournamentByIndex(LPGIdx + 2);
          }
          break;

        case 'LPG_LESS_MPG':
          templateSlot3 = getTournamentByFee(mostPlayedFee);
          if (mostPlayedIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(mostPlayedIdx - 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx - 2);
          } else if (mostPlayedIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = getTournamentByIndex(mostPlayedIdx + 2);
          }
          break;

        case 'MPG_NOT_AVAILABLE':
          templateSlot3 = getLPGTournament();
          if (LPGIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(LPGIdx - 1);
            templateSlot1 = getTournamentByIndex(LPGIdx - 2);
          } else if (LPGIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = getTournamentByIndex(LPGIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = getTournamentByIndex(LPGIdx + 2);
          }
          break;

        case 'LPG_NOT_AVAILABLE': // New users
          // Use lowest $ game logic
          templateSlot3 = getTournamentByIndex(0); // Lowest $ Game
          templateSlot2 = getTournamentByIndex(1); // Lowest $ Game + 1
          templateSlot1 = getTournamentByIndex(2); // Lowest $ Game + 2
          break;

        default:
          templateSlot3 = getTournamentByIndex(0);
          templateSlot2 = getTournamentByIndex(1);
          templateSlot1 = getTournamentByIndex(2);
      }
    } else {
      // Group 2 logic (LE % >= 10%)
      let liveEventTournament = currentLiveEvent;
      const hasLiveEvent = liveEventTournament && isLiveTournament(liveEventTournament);

      switch (lpgMpgRelation) {
        case 'LPG_EQUALS_MPG':
          templateSlot3 = getTournamentByFee(mostPlayedFee);
          if (mostPlayedIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(mostPlayedIdx - 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx - 2);
          } else if (mostPlayedIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx + 2);
          }
          break;

        case 'LPG_GREATER_MPG':
          templateSlot3 = getLPGTournament();
          if (LPGIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(LPGIdx - 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx - 2);
          } else if (LPGIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx + 2);
          }
          break;

        case 'LPG_LESS_MPG':
          templateSlot3 = getTournamentByFee(mostPlayedFee);
          if (mostPlayedIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(mostPlayedIdx - 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx - 2);
          } else if (mostPlayedIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(mostPlayedIdx + 1);
            templateSlot1 = hasLiveEvent
              ? liveEventTournament
              : getTournamentByIndex(mostPlayedIdx + 2);
          }
          break;

        case 'MPG_NOT_AVAILABLE':
          templateSlot3 = getLPGTournament();
          if (LPGIdx === sortedFees.length - 1) {
            // Last element: go backwards for both templateSlot2 and templateSlot1
            templateSlot2 = getTournamentByIndex(LPGIdx - 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx - 2);
          } else if (LPGIdx === sortedFees.length - 2) {
            // Second to last: templateSlot2 goes forward (+1), templateSlot1 goes backward (-1)
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx - 1);
          } else {
            // Normal case: go forward for both
            templateSlot2 = getTournamentByIndex(LPGIdx + 1);
            templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(LPGIdx + 2);
          }
          break;

        case 'LPG_NOT_AVAILABLE': // New users
          // Use lowest $ game logic
          templateSlot3 = getTournamentByIndex(0); // Lowest $ Game
          templateSlot2 = getTournamentByIndex(1); // Lowest $ Game + 1
          templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(2); // Live event or Lowest $ Game + 2
          break;

        default:
          templateSlot3 = getTournamentByIndex(0);
          templateSlot2 = getTournamentByIndex(1);
          templateSlot1 = hasLiveEvent ? liveEventTournament : getTournamentByIndex(2);
      }
    }

    // Handle edge cases where tournaments might be null due to index out of bounds
    // Fill missing tournaments with available ones, prioritizing previous indices when near the end
    const tournaments = [templateSlot3, templateSlot2, templateSlot1];
    for (let i = 0; i < tournaments.length; i++) {
      if (!tournaments[i]) {
        // Try to find a replacement from available tournaments
        let replacementIdx = i;
        while (replacementIdx < sortedFees.length && !tournaments[i]) {
          const candidate = getTournamentByIndex(replacementIdx);
          if (candidate && !tournaments.includes(candidate)) {
            tournaments[i] = candidate;
          }
          replacementIdx++;
        }

        // If still not found, try going backwards
        if (!tournaments[i]) {
          replacementIdx = sortedFees.length - 1 - i;
          while (replacementIdx >= 0 && !tournaments[i]) {
            const candidate = getTournamentByIndex(replacementIdx);
            if (candidate && !tournaments.includes(candidate)) {
              tournaments[i] = candidate;
            }
            replacementIdx--;
          }
        }
      }
    }

    [templateSlot3, templateSlot2, templateSlot1] = tournaments;

    // Store live event separately to maintain its position
    const liveEventInRT1 = templateSlot1 && isLiveTournament(templateSlot1) ? templateSlot1 : null;

    // Create array with proper indexing to maintain slot positions
    let recommendedTournaments = [templateSlot3, templateSlot2, templateSlot1];

    // Remove duplicates while preserving priority and slot positions
    const seenIds = new Set();
    const seenFees = new Set();
    const duplicateIndices = [];

    for (let i = 0; i < recommendedTournaments.length; i++) {
      const tournament = recommendedTournaments[i];
      if (tournament && (seenIds.has(tournament.id) || seenFees.has(tournament.cash_fee))) {
        // Mark this slot for replacement (duplicate ID or duplicate fee)
        duplicateIndices.push(i);
        recommendedTournaments[i] = null;
      } else if (tournament) {
        seenIds.add(tournament.id);
        seenFees.add(tournament.cash_fee);
      }
    }

    // Fill duplicate slots with replacement tournaments
    for (const duplicateIndex of duplicateIndices) {
      // Find the best unused tournament to replace the duplicate
      let replacementTournament = null;

      // Get current fees of non-null tournaments
      const currentFees = recommendedTournaments
        .filter((t) => t !== null)
        .map((t) => t.cash_fee)
        .sort((a, b) => a - b);

      const maxCurrentFee = Math.max(...currentFees);

      // Strategy: Find the next logical fee progression
      // 1. First try to find a tournament with fee higher than current max
      // 2. If none found, pick the highest available unused tournament
      // 3. If still none, fall back to any unused tournament

      // Try to find next higher fee tournament
      for (const tournament of sortedTournaments) {
        if (
          !seenIds.has(tournament.id) &&
          !seenFees.has(tournament.cash_fee) &&
          tournament.cash_fee > maxCurrentFee
        ) {
          replacementTournament = tournament;
          break;
        }
      }

      // If no higher fee found, pick the highest unused tournament
      if (!replacementTournament) {
        for (let i = sortedTournaments.length - 1; i >= 0; i--) {
          const tournament = sortedTournaments[i];
          if (!seenIds.has(tournament.id) && !seenFees.has(tournament.cash_fee)) {
            replacementTournament = tournament;
            break;
          }
        }
      }

      if (replacementTournament) {
        recommendedTournaments[duplicateIndex] = replacementTournament;
        seenIds.add(replacementTournament.id);
        seenFees.add(replacementTournament.cash_fee);
      }
    }

    // Filter out remaining null values
    recommendedTournaments = recommendedTournaments.filter((t) => t);

    // Sort cash tournaments, but keep live event position fixed
    if (liveEventInRT1) {
      // Separate cash tournaments from live event
      const cashTournaments = recommendedTournaments.filter((t) => !isLiveTournament(t));

      // Sort cash tournaments
      if (!isAscending) {
        cashTournaments.sort((a, b) => b.cash_fee - a.cash_fee);
      } else {
        cashTournaments.sort((a, b) => a.cash_fee - b.cash_fee);
      }

      // Reconstruct array with live event always at 3rd position (index 2)
      recommendedTournaments = [...cashTournaments];
      if (recommendedTournaments.length >= 2) {
        recommendedTournaments.splice(2, 0, liveEventInRT1); // Insert at index 2 (3rd position)
      } else {
        recommendedTournaments.push(liveEventInRT1);
      }
    } else {
      // No live event, just sort normally
      if (!isAscending) {
        recommendedTournaments.sort((a, b) => b.cash_fee - a.cash_fee);
      } else {
        recommendedTournaments.sort((a, b) => a.cash_fee - b.cash_fee);
      }
    }

    // Assign badge text for slots - create copies to avoid affecting other sections
    const slotBadges = [
      ClientPropertyConstants.getRecommendedTemplateV2Slot1BadgeText(),
      ClientPropertyConstants.getRecommendedTemplateV2Slot2BadgeText(),
      ClientPropertyConstants.getRecommendedTemplateV2Slot3BadgeText(),
    ];
    recommendedTournaments = recommendedTournaments.map((t, idx) => {
      if (t) {
        // Create a new Tournament instance to avoid modifying the original
        const clonedTournament = new Tournament(t);
        clonedTournament.highlight_text = slotBadges[idx] || '';

        // Ensure discount data is preserved from the latest API response
        // Find the source tournament with potentially updated discount data
        const sourceTournament = sortedTournaments.find((st) => String(st.id) === String(t.id));
        if (sourceTournament && sourceTournament.cash_fee_discount !== undefined) {
          clonedTournament.cash_fee_discount = sourceTournament.cash_fee_discount;
        }

        return clonedTournament;
      }
      return t;
    });

    // Ensure we have exactly 3 tournaments
    while (recommendedTournaments.length < 3) {
      // Find unused tournaments from sortedTournaments
      const usedIds = new Set(recommendedTournaments.map((t) => t.id));
      const usedFees = new Set(recommendedTournaments.map((t) => t.cash_fee));
      let foundReplacement = false;

      // Look for any unused tournament (prioritizing ascending order)
      let replacementTournament = null;
      for (const tournament of sortedTournaments) {
        if (!usedIds.has(tournament.id) && !usedFees.has(tournament.cash_fee)) {
          replacementTournament = tournament;
          break;
        }
      }

      if (replacementTournament) {
        foundReplacement = true;
        recommendedTournaments.push(replacementTournament);
      }

      if (!foundReplacement) {
        // Fallback: allow duplicate fees if we really need to fill slots
        for (const tournament of sortedTournaments) {
          if (!usedIds.has(tournament.id)) {
            recommendedTournaments.push(tournament);
            foundReplacement = true;
            break;
          }
        }
      }

      if (!foundReplacement) {
        break; // No tournaments available at all
      }
    }

    // Limit to 3 tournaments
    recommendedTournaments = recommendedTournaments.slice(0, 3);

    // Show only first tournament if category is not expanded
    const displayTournaments = !this.isCategoryExpanded('RECOMMENDEDV2')
      ? recommendedTournaments.slice(0, 1)
      : recommendedTournaments;

    let recommendedSection = new MatchSelectListSection({
      type: 'RECOMMENDEDV2',
      name: 'Recommended',
      description: 'Recommended match section for the user',
      length: recommendedTournaments.length,
      data: displayTournaments,
    });
    recommendedSection.configureAccordionForRecommendedTemplate(3);
    dataBlob.unshift(recommendedSection);

    // Update store's internal data with the new discount information to ensure UI consistency
    this._updateStoreDataWithDiscountedTournaments(cashTemplates.tournaments, sortedTournaments);

    // CRITICAL: Force update the dataBlob recommended section with fresh store data
    // Find and update the recommended section that was just added
    const builtRecommendedSection = dataBlob.find((section) => section.type === 'RECOMMENDEDV2');
    if (builtRecommendedSection) {
      this._refreshRecommendedSectionWithStoreData(
        builtRecommendedSection,
        cashTemplates.tournaments
      );
    }
  },

  // Helper method to refresh recommended section tournaments with updated store data
  _refreshRecommendedSectionWithStoreData(recommendedSection, originalTournaments) {
    if (!this._data || !Array.isArray(this._data) || !recommendedSection?.data) return;

    // Find the updated CASH category in store data
    const updatedCashCategory = this._data.find((category) => category.type === 'CASH');
    if (!updatedCashCategory?.tournaments) return;

    // Update each tournament in the recommended section with latest store data
    recommendedSection.data.forEach((tournament, index) => {
      if (tournament) {
        // Find the matching tournament in the updated store data
        const updatedTournament = updatedCashCategory.tournaments.find(
          (t) => String(t.id) === String(tournament.id)
        );

        if (
          updatedTournament &&
          updatedTournament.cash_fee_discount !== tournament.cash_fee_discount
        ) {
          // Update the discount data directly on the recommended tournament
          tournament.cash_fee_discount = updatedTournament.cash_fee_discount;
        }
      }
    });
  },

  // Helper method to update internal store data with discounted tournament information
  _updateStoreDataWithDiscountedTournaments(originalTournaments, updatedTournaments) {
    if (!this._data || !Array.isArray(this._data)) return;

    // Find the CASH category in internal data
    const cashCategory = this._data.find((category) => category.type === 'CASH');
    if (!cashCategory || !cashCategory.tournaments) return;

    let hasUpdates = false;

    // Update each tournament in the cash category with discount information
    cashCategory.tournaments.forEach((tournament) => {
      // Find the corresponding updated tournament with potential discount info
      const updatedTournament = updatedTournaments.find(
        (t) => String(t.id) === String(tournament.id)
      );
      if (updatedTournament) {
        // Store previous discount state for comparison
        const previousDiscount = tournament.cash_fee_discount;

        // Always sync the discount state from the latest sorted tournament data
        tournament.cash_fee_discount = updatedTournament.cash_fee_discount;

        // Check if discount state changed
        const discountChanged =
          JSON.stringify(previousDiscount) !== JSON.stringify(updatedTournament.cash_fee_discount);

        if (discountChanged) {
          hasUpdates = true;
          this._hasDiscountUpdates = true; // Flag for shouldRefreshData to detect
        }
      }
    });

    // Only trigger UI update if there were actual changes
    if (hasUpdates) {
      this.emitChange();
    }
  },

  _insertTemplateSets(
    category: Object,
    dataBlob: Array<MatchSelectListSection>,
    shouldFilterToZTemplate: boolean,
    shouldFilterToOneTemplateSet: boolean
  ) {
    if (
      category.tournament_template_sets instanceof Array &&
      category.type == SYNC &&
      !shouldFilterToZTemplate &&
      Platform.OS !== 'web'
    ) {
      if (shouldFilterToOneTemplateSet && category.tournament_template_sets.length > 0) {
        this._addTournamentTemplateSetToCategoryInBlob(
          category.tournament_template_sets[0],
          category,
          dataBlob
        );
        return;
      }
      category.tournament_template_sets.forEach((tournamentTemplateSet, sectionIndex) => {
        tournamentTemplateSet.index = sectionIndex;
        this._addTournamentTemplateSetToCategoryInBlob(tournamentTemplateSet, category, dataBlob);
      });
    }
  },

  _insertSingleTemplates(
    category: SKZTournamentCategoryRecord,
    dataBlob: Array,
    shouldFilterToZTemplate: boolean
  ) {
    if (!_.get(category, 'tournaments.length') || category.type == SYNC) {
      return;
    }

    if (category.type == 'LIVE' && this._shouldDisplaySearchResults) {
      this.structureDataForSearching(dataBlob, shouldFilterToZTemplate);
    }

    let tournaments = category.tournaments;
    // 🎯 FIX: Disable template filtering for FTUEV2 based on user path threshold
    const isFTUEV2Enabled = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    const totalGamesPlayed = this._totalGamesPlayed ?? 0;
    const userPath = FTUEController.getUserPathSelection();
    const gameCountThreshold = userPath === 'PRO' ? 1 : 2;
    const shouldDisableTemplateFiltering = isFTUEV2Enabled && totalGamesPlayed < gameCountThreshold;

    if (isFTUEV2Enabled && category.type === 'Z' && !shouldDisableTemplateFiltering) {
      // If Training flow has any 1v1, keep only 1v1 (one pass, O(n))
      const has1v1 = tournaments.some((t) => t.max_players == 2);
      if (has1v1) {
        // 🔥 FIX: Always preserve highlighted tournaments regardless of max_players
        tournaments = tournaments.filter(
          (t) => t.max_players == 2 || t.highlight === true || t.highlighted === true
        );
      }

      // If after all rules nothing remains, keep at least one so Z section doesn't vanish
      if (tournaments.length === 0 && category.tournaments.length > 0) {
        tournaments = [category.tournaments[0]];
        // ensure it can render when collapsed
        tournaments[0].featured = true;
      }
    }

    // Special sorting for live brackets
    if (category.type === 'LIVE_BRACKET') {
      tournaments.sort((a, b) => {
        // First, check if either tournament is a live bracket
        const aIsLiveBracket = a.isLiveBracket();
        const bIsLiveBracket = b.isLiveBracket();

        // If both are live brackets
        if (aIsLiveBracket && bIsLiveBracket) {
          // Check if either is in progress
          const aIsInProgress = a.isPlayerRegistered() && a.bracket?.user_state === 'READY';
          const bIsInProgress = b.isPlayerRegistered() && b.bracket?.user_state === 'READY';

          // If one is in progress and the other isn't, in progress comes first
          if (aIsInProgress !== bIsInProgress) {
            return aIsInProgress ? -1 : 1;
          }

          // If both are in progress or both are not in progress
          if (aIsInProgress && bIsInProgress) {
            // If both are in progress, sort by start time
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          }

          // If neither is in progress, check registration status
          if (a.isPlayerRegistered() !== b.isPlayerRegistered()) {
            return a.isPlayerRegistered() ? -1 : 1;
          }

          // If both are registered or both are not registered, sort by start time
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        }

        // If only one is a live bracket, it comes first
        if (aIsLiveBracket !== bIsLiveBracket) {
          return aIsLiveBracket ? -1 : 1;
        }

        // For non-live bracket tournaments, use existing sorting logic
        if (this.shouldShowLowestEntryFeeTournamentFirst(category)) {
          return a.cash_fee - b.cash_fee;
        }
        return 0;
      });
    } else if (this.shouldShowLowestEntryFeeTournamentFirst(category)) {
      // For non-LIVE categories, only sort by cash_fee
      tournaments.sort((a, b) => a.cash_fee - b.cash_fee);
    }

    // Set featured flag for the first tournament
    if (tournaments && tournaments.length > 0) {
      if (category.type === 'LIVE' || category.type === 'LIVE_BRACKET') {
        // Only set featured for live brackets in LIVE/LIVE_BRACKET category
        if (tournaments[0]?.isLiveBracket()) {
          tournaments[0].featured = true;
        }
      } else if (this.shouldShowLowestEntryFeeTournamentFirst(category)) {
        // Set featured for first tournament in other categories when sorting by cash_fee
        tournaments[0].featured = true;
      } else if (category.type === 'Z') {
        // For practice tournaments, mark the first non-Z+BC tournament as featured
        const firstRegularZ = tournaments.find(
          (t) => !new Tournament(t).isAnyZEntryBonusCashPrizeTournament()
        );
        if (firstRegularZ) {
          firstRegularZ.featured = true;
        } else {
          // Fallback: if only Z+BC tournaments exist, feature the first one
          tournaments[0].featured = true;
        }
      }
    }

    const isTrainingMatch = category.type === 'Z' && FTUEConversionsController.isTrainingMatch;
    if (isTrainingMatch) {
      // Exclude any tournaments that aren't 1v1 during a training match
      tournaments = tournaments.filter((tournament) => tournament.max_players == 2);
    }

    const minZEntryFee = Math.min(
      ...tournaments.map((tournament) => {
        // If this is a cash tournament, exclude it from the minimum calculation
        if (tournament.cash_fee) return Number.MAX_SAFE_INTEGER;

        // If this tournament costs 0z, also exclude it (if z_fee is 0, the ||
        // will make it fall back to the max int value)
        return tournament.z_fee || Number.MAX_SAFE_INTEGER;
      })
    );

    const tournamentCount = tournaments.length;
    tournaments.forEach((tournament, sectionIndex) => {
      if (!this._shouldDisplaySearchResults || !tournament.live_event) {
        tournament.index = sectionIndex;
        const tournamentCreatorId = _.get(tournament, 'creator.user_id');
        if (tournamentCreatorId && BlockedUsersStore.isUserBlocked(tournamentCreatorId)) {
          return;
        }

        if (
          this.shouldHideTournament(tournament, {
            minZEntryFee,
            category,
            sectionIndex,
            tournamentCount,
          })
        ) {
          if (tournament.featured && sectionIndex < category.tournaments.length - 1) {
            category.tournaments[sectionIndex + 1].featured = true;
          }
          return;
        }

        // Ensure the correct training match is featured
        if (!tournament.featured && isTrainingMatch) {
          tournament.featured =
            sectionIndex === FTUEConversionsController.trainingMatchTemplateIndex;
        }

        this._addTournamentToCategoryInBlob(
          tournament,
          category,
          dataBlob,
          shouldFilterToZTemplate
        );
      }
    });

    //After all tournaments are processed, reorder Z section to put Z+BC tournaments as second
    if (category.type === 'Z') {
      this._reorderZSectionForBonusCashTournaments(dataBlob, category);
    }
  },

  getLastSectionType() {
    return this.lastSectionType;
  },

  shouldRefreshData(shouldFilterToZTemplate: boolean): boolean {
    let shouldRefresh = false;
    const sectionsData = this.getSectionsData(shouldFilterToZTemplate);
    if (sectionsData) {
      _.forEach(sectionsData, (section, index) => {
        if (section.type !== SKZConstants.SKILLZ_NEWS_BANNER && section.containsLiveBracket()) {
          shouldRefresh = true;
        }
      });
    }

    // Also check if discount data needs refreshing
    if (this._hasDiscountUpdates) {
      shouldRefresh = true;
      this._hasDiscountUpdates = false; // Reset the flag
    }

    return shouldRefresh;
  },

  structureDataForSearching(dataBlob, shouldFilterToZTemplate: boolean) {
    let categoryForSection = null;
    if (this._data instanceof Array) {
      this._data.some((category, index, array) => {
        if (_.get(category, 'tournaments[0].live_event')) {
          categoryForSection = category;
          return true;
        } else {
          return false;
        }
      });

      if (this._searchResults && this._searchResults.length) {
        this._searchResults.forEach((tournament, index) => {
          this._addTournamentToCategoryInBlob(
            tournament,
            categoryForSection,
            dataBlob,
            shouldFilterToZTemplate
          );
        });
      } else {
        const nullTournament = {id: 0};
        this._addTournamentToCategoryInBlob(
          nullTournament,
          categoryForSection,
          dataBlob,
          shouldFilterToZTemplate
        );
      }
    }
  },

  /**
   * Return true if the category has been expanded, false if category has been colapsed
   * @param {string} categoryType
   */
  isCategoryExpanded(categoryType: string): boolean {
    return categoryType in this._expandedCategories && this._expandedCategories[categoryType];
  },

  _getSectionListForCategory(
    dataBlob: Array<MatchSelectListSection>,
    category: unknown,
    isTemplate = false
  ): MatchSelectListSection {
    let existingSection = null;

    _.forEach(dataBlob, (section: MatchSelectListSection) => {
      if (section.type == category.type) {
        existingSection = section;
      }
    });

    const length = isTemplate
      ? category.tournament_template_sets.length
      : category.tournaments.length;

    if (!existingSection) {
      existingSection = new MatchSelectListSection({
        type: category.type,
        name: category.name,
        description: category.description,
        _id: category._id,
        length: length,
        data: [],
      });
      existingSection.configureAccordion(category);
      dataBlob.push(existingSection);
    }
    return existingSection;
  },

  _shouldIncludeFriendTemplate(tournament: Tournament, section: MatchSelectListSection): boolean {
    if (!tournament.isFriendTournament()) {
      return false;
    }

    if (tournament.creator == null) {
      return true;
    } else {
      return !section.containsIncomingFriendInvite();
    }
  },

  _addTournamentTemplateSetToCategoryInBlob(tournamentTemplateSet, category, dataBlob) {
    const section = this._getSectionListForCategory(dataBlob, category, true);
    section.data.push(tournamentTemplateSet);
  },

  _addTournamentToCategoryInBlob(tournament, category, dataBlob, shouldFilterToZTemplate: boolean) {
    if (!category) return;

    tournament = new Tournament(tournament);
    tournament.category_type = category.type;

    // Keep Z+BC feature status when flag is ON (prevents Z section from disappearing when collapsed)
    const keepPracticeVisible = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    if (!keepPracticeVisible || category.type !== 'Z') {
      if (
        tournament.isAnyZEntryBonusCashPrizeTournament &&
        tournament.isAnyZEntryBonusCashPrizeTournament()
      ) {
        tournament.featured = false;
      }
    }

    const section = this._getSectionListForCategory(dataBlob, category);
    let shouldShowCell = false;
    if (!shouldFilterToZTemplate) {
      shouldShowCell =
        this.isCategoryExpanded(category.type) ||
        tournament.featured ||
        this._shouldIncludeFriendTemplate(tournament, section);
    } else {
      // 🔥 FIX: RP-6891 - Show Z tournaments including limited use ones when FTUE V2 is enabled
      if (ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
        shouldShowCell = tournament.category_type == 'Z';
        // } else {
        //   // Only show a regular Z tournament in FTUE (no limited use)
        //   shouldShowCell = tournament.category_type == 'Z' && !tournament.isLimitedUseTournament();
      }
    }

    if (!shouldShowCell) return;

    if (tournament.featured) {
      tournament.highlight_text = category.highlight_text;
    }

    if (this._highlightedMatch && tournament.id == this._highlightedMatch) {
      if (this._highlightedMatchText) {
        tournament.highlight_text = this._highlightedMatchText;
      }
      tournament.highlighted = true;
    }

    if (!this._shouldFilterExpiredLiveEvent(tournament)) {
      //Check for duplicates already entered before inserting.
      const hasDuplicate = section.hasDuplicate(tournament);
      const isSelected = this.isCategoryExpanded(category.type);
      const hasRow = section.hasData();
      const shouldIncludeFriendTemplate = this._shouldIncludeFriendTemplate(tournament, section);

      if (!hasDuplicate && (isSelected || !hasRow || shouldIncludeFriendTemplate)) {
        // If we're in FTUE, we need the tournament to be featured so MatchSelectCell can highlight it. If there are Limited Use Tournaments, then the lowest Z Tournament we want to highlight is not featured, so feature it here.
        if (shouldFilterToZTemplate) {
          tournament.featured = true;
        }

        if (this.shouldLimitDisplayedTournaments(category, section)) {
          return;
        }

        // Add all tournaments normally for now - we'll reorder Z section later
        section.data.push(tournament);
      }
    } else if (tournament.featured && !this.isCategoryExpanded(category.type)) {
      // Tournament is featured, and category isn't selected, but needs to
      // be filtered, so choose random tournament to feature.
      const replacement = this._findReplacementForFeaturedTournament(category);
      if (replacement) {
        replacement.featured = true;
        this._addTournamentToCategoryInBlob(
          replacement,
          category,
          dataBlob,
          shouldFilterToZTemplate
        );
      }
    }
  },

  /**
   * Reorders the Z section to ensure Z+BC tournaments appear as the second template.
   * This runs after all tournaments have been added to maintain proper positioning.
   */
  _reorderZSectionForBonusCashTournaments(dataBlob, category) {
    const section = this._getSectionListForCategory(dataBlob, category);
    if (!section.data || section.data.length <= 1) {
      return; // No reordering needed if we have 0 or 1 tournaments
    }

    // Separate Z+BC tournaments from regular Z tournaments
    const zbcTournaments = [];
    const regularTournaments = [];

    section.data.forEach((tournament) => {
      if (
        tournament.isAnyZEntryBonusCashPrizeTournament &&
        tournament.isAnyZEntryBonusCashPrizeTournament()
      ) {
        zbcTournaments.push(tournament);
      } else {
        regularTournaments.push(tournament);
      }
    });

    // If we have both types, reorder: first regular tournament, then Z+BC tournaments, then remaining regular tournaments
    if (zbcTournaments.length > 0 && regularTournaments.length > 0) {
      const reorderedData = [];

      // Add first regular tournament
      reorderedData.push(regularTournaments[0]);

      // Add all Z+BC tournaments starting at position 1 (second position)
      reorderedData.push(...zbcTournaments);

      // Add remaining regular tournaments
      reorderedData.push(...regularTournaments.slice(1));

      // Update the section data
      section.data = reorderedData;
    }
  },

  shouldLimitDisplayedTournaments(category, section) {
    // 🎯 FIX: Don't limit templates for CASH and Z based on user path threshold and FTUEV2 is enabled
    const isFTUEV2Enabled = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    const totalGamesPlayed = this._totalGamesPlayed ?? 0;
    const userPath = FTUEController.getUserPathSelection();
    const gameCountThreshold = userPath === 'PRO' ? 1 : 2;
    const shouldDisableLimiting =
      isFTUEV2Enabled &&
      totalGamesPlayed < gameCountThreshold &&
      (category.type === CASH || category.type === 'Z');

    if (shouldDisableLimiting) return false;

    if (category.type !== CASH) return false;
    if (section.data.length < 3) return false;
    return [NewUserMatchFilter.EXPAND_Z, NewUserMatchFilter.EXPAND_PRO].includes(
      this._newUserMatchFilter
    );
  },

  setExpandedHeightOffset(height: number) {
    this._expandedHeightOffset = height;
  },

  getCategoryExpanded(categoryType: string, isFTUE = false) {
    // During the FTUE, we won't expand any categories
    if (isFTUE) return;

    switch (this._newUserMatchFilter) {
      case NewUserMatchFilter.EXPAND_Z: {
        // Expand Z and collapse cash templates for new users
        if (categoryType === Z_TEMPLATE) return true;
        if (categoryType === CASH) return false;
        return;
      }
      case NewUserMatchFilter.EXPAND_PRO:
      case NewUserMatchFilter.ALL_PRO_ENTRY_FEES:
      case NewUserMatchFilter.SPECIAL_MODES: {
        // We only want to force expand the cash templates for these match filters
        if (categoryType !== CASH) return;

        // Check that the user's device is tall enough to show the Z templates
        // with 3 cash templates visible; their phone needs to be at least 5.8",
        // e.g. iPhone X
        const meetsHeightRequirement = Sizes.meetsSizeRequirements({
          minHeight: IPHONE_X_HEIGHT + this._expandedHeightOffset,
        });
        return meetsHeightRequirement ? true : undefined;
      }
      default:
        return;
    }
  },

  shouldShowLowestEntryFeeTournamentFirst(category: any) {
    if (category.type !== CASH) return false;
    return [NewUserMatchFilter.EXPAND_Z, NewUserMatchFilter.EXPAND_PRO].includes(
      this._newUserMatchFilter
    );
  },

  shouldHideTournament(
    tournament: Tournament,
    {
      minZEntryFee = 0,
      category,
      sectionIndex,
      tournamentCount,
    }: {
      minZEntryFee?: number;
      category: {type: string};
      sectionIndex: number;
      tournamentCount: number;
    }
  ) {
    if (ClientPropertyConstants.getFTUEV2FeatureEnabled() && category.type === 'Z') {
      return false;
    }
    const isPracticeMatch = category.type === 'Z';

    if (isPracticeMatch && !tournament.z_fee && tournament.prizes.find((prize) => prize.z_prize)) {
      // Free tournaments that award Z should be hidden if the user doesn't meet
      // the creation requirement (7 days old or less) or has more Z than the
      // lowest Z entry fee tournament (excluding the free tournament)
      return !this.meetsUserCreationRequirement() || this._zBalance >= minZEntryFee;
    }

    // For a training match, ignore any tournaments that don't match the
    // trainingMatchTemplateIndex
    // If we don't have enough templates, however, we'll ignore this check, e.g.
    // if we only have 2 templates and the trainingMatchTemplateIndex is 2
    const {trainingMatchTemplateIndex} = FTUEConversionsController;
    const shouldSkipIndex =
      isPracticeMatch && FTUEConversionsController.isTrainingMatch
        ? trainingMatchTemplateIndex < tournamentCount &&
          sectionIndex !== trainingMatchTemplateIndex
        : false;
    if (shouldSkipIndex) {
      return true;
    }

    // Don't hide non-special mode tournaments
    if (!tournament.limited_use) return false;

    //Special Handling for limited Use tournaments if flag is enabled for user and this is FTUE flow
    if (ClientPropertyConstants.getFTUEV2FeatureEnabled() && category.type === 'Z') {
      return false;
    }
    // Special modes should be hidden for any match filter that's NOT special
    // modes or none
    // return ![NewUserMatchFilter.SPECIAL_MODES, NewUserMatchFilter.NONE].includes(
    //   this._newUserMatchFilter
    // );

    const isLimitedUse = !!tournament.limited_use;
    if (isLimitedUse) {
      const isPractice = category.type === 'Z';
      if (isPractice && this._newUserMatchFilter === NewUserMatchFilter.EXPAND_Z) {
        return false; // keep Z visible
      }
      return ![NewUserMatchFilter.SPECIAL_MODES, NewUserMatchFilter.NONE].includes(
        this._newUserMatchFilter
      );
    }
    return false;
  },

  meetsUserCreationRequirement(maxDaysOld = 7) {
    if (!this._joinDate) return false;
    const daysOld = moment().diff(this._joinDate, 'days');
    return daysOld <= maxDaysOld;
  },

  _findReplacementForFeaturedTournament(category) {
    const firstReplacement = _.find(category.tournaments, (tournament) => {
      //Find the first tournament that isn't featured and doesn't need to be filtered.
      const isReplacement = !tournament.featured && !this._shouldFilterExpiredLiveEvent(tournament);
      return isReplacement;
    });

    if (firstReplacement) {
      return firstReplacement;
    } else {
      return null;
    }
  },

  _shouldFilterExpiredLiveEvent(tournament: Tournament): boolean {
    if (!tournament.live_event) {
      return false;
    }

    const now = new Date();
    const end = new Date(tournament.end);

    // live bracket rules
    if (tournament.bracket) {
      // Filter out live brackets that haven't reached their show start time yet
      // But allow live brackets without show_start_time to be shown (they show register button directly)
      if (
        tournament.isLiveBracket() &&
        !tournament.shouldShowLiveBracket() &&
        tournament.bracket?.show_start_time
      ) {
        return true;
      }
      // User is ready to play but time to play has expired
      if (tournament.bracket.user_expires_at && tournament.bracket.user_state == 'READY') {
        const expiresAt = new Date(tournament.bracket.user_expires_at);
        return expiresAt < now;
      }

      /**
       * If the tournament is a live bracket, has started and the player is not registered,
       * then the tournament will not be shown. The main reason for that is to avoid the
       * stream flow for live brackets during GDC, after GDC we may allow stream again.
       *
       * This is controlled by the client property 'live.brackets.skip.stream.flow', defaults to true.
       */
      if (
        tournament.isLiveBracket() &&
        tournament.hasTournamentStarted() &&
        !tournament.isPlayerRegistered() &&
        ClientPropertyConstants.getLiveBracketsSkipStreamFlow()
      ) {
        return true;
      }

      // If the tournament is a live bracket and the player has lost the match, then the tournament will not be shown.
      if (
        tournament.isLiveBracket() &&
        tournament.isPlayerMatchFinished() &&
        !tournament.isLiveEventBeingFinalized()
      ) {
        return true;
      }

      return false;
    }

    // If the tournament is a live event and it has ended, then the tournament will not be shown.
    return end < now;
  },

  getTournamentById(id: number | string) {
    let foundTournament: Tournament | undefined = undefined;
    _.forEach(this._data, (category) => {
      if (category.tournament_template_sets) {
        _.forEach(category.tournament_template_sets.tournaments, (tournament) => {
          if (tournament.id == id || tournament?.live_event?.id == id) {
            foundTournament = new Tournament(tournament);
            return;
          }
        });
      } else {
        _.forEach(category.tournaments, (tournament) => {
          if (tournament.id == id || tournament?.live_event?.id == id) {
            foundTournament = new Tournament(tournament);
            return;
          }
        });
      }
      if (foundTournament) {
        return;
      }
    });
    return foundTournament;
  },

  /**
   * Return true if the category has been toggled before
   * @param {string} categoryType
   */
  hasCategoryBeenToggled(categoryType): boolean {
    return this._expandedCategories[categoryType] !== undefined;
  },

  actionTypeHandlers: {
    FETCH_MATCH_LIST: function (action) {
      this.fetchMatchList(action.payload.showLoading, action.payload.isFTUE);
    },

    SELECT_MATCH_LIST: function (action) {
      this.selectMatchCategory(action.payload.matchCategoryType);
    },

    SEARCH_FOR_EVENTS: function (action) {
      this.searchForEvents(action.payload.searchTerm);
    },

    HIGHLIGHT_MATCH: function (action) {
      this.updateDataToHighlightMatch(action.payload);
    },
  },

  isFriendsTournamentsEnabled(): boolean {
    return ClientPropertyConstants.getFriendsTournamentEnabled();
  },

  /**
   * Ensures each section has at least one featured tournament if it has any tournaments.
   * This prevents sections from disappearing when collapsed.
   */
  _ensureSectionsHaveFeaturedTournaments(dataBlob) {
    dataBlob.forEach((section) => {
      if (section.data && section.data.length > 0) {
        // Check if this section has any featured tournaments
        const hasFeaturedTournament = section.data.some((tournament) => tournament.featured);

        // If no featured tournament, mark the first tournament as featured
        if (!hasFeaturedTournament) {
          section.data[0].featured = true;
        }
      }
    });
  },
});

export default PlayableMatchesStore;
